import React, { useState, useEffect } from 'react';
import { Cookie } from 'lucide-react';

type CookieConsentStatus = 'accepted' | 'declined' | 'customized' | null;

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const notifyCookieConsentChange = (settings: CookieSettings) => {
  window.dispatchEvent(new CustomEvent('onzoe-cookie-consent-changed', { detail: settings }));
};

export const CookieConsent: React.FC = () => {
  const [cookieConsent, setCookieConsent] = useState<CookieConsentStatus>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // In a real app, you'd use localStorage here
    const consent = localStorage.getItem('cookieConsent') as CookieConsentStatus;
    const savedSettings = localStorage.getItem('cookieSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
    setCookieConsent(consent);
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setSettings(allAccepted);
    setCookieConsent('accepted');
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieSettings', JSON.stringify(allAccepted));
    notifyCookieConsentChange(allAccepted);
  };

  const handleDeclineAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setSettings(onlyNecessary);
    setCookieConsent('declined');
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieSettings', JSON.stringify(onlyNecessary));
    notifyCookieConsentChange(onlyNecessary);
  };

  const handleCustomize = () => {
    setShowSettings(!showSettings);
  };

  const handleSaveSettings = () => {
    setCookieConsent('customized');
    setShowSettings(false);
    localStorage.setItem('cookieConsent', 'customized');
    localStorage.setItem('cookieSettings', JSON.stringify(settings));
    notifyCookieConsentChange(settings);
  };

  const updateSetting = (key: keyof CookieSettings, value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (cookieConsent) {
    return null;
  }

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-4 right-4 z-50 animate-slide-up max-w-sm" role="region" aria-label="Cookie preferences">
        <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-2xl">
          <div className="p-4">
            <div className="flex items-start space-x-3">
              <Cookie className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" aria-hidden="true" />

              <div className="flex-1 min-w-0">
                <p className="text-gray-300 text-xs leading-relaxed">
                  We use cookies to enhance your experience and analyze site traffic.
                </p>

                {showSettings && (
                  <div id="cookie-settings" className="bg-slate-800 rounded-md p-3 mt-2 border border-slate-600">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white">Necessary</span>
                        <span className="text-[10px] text-green-400 font-medium">Always on</span>
                      </div>
                      {Object.entries({
                        analytics: 'Analytics',
                        marketing: 'Marketing',
                        functional: 'Functional'
                      }).map(([key, name]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-xs text-white">{name}</span>
                          <button
                            type="button"
                            onClick={() => updateSetting(key as keyof CookieSettings, !settings[key as keyof CookieSettings])}
                            role="switch"
                            aria-label={`${name} cookies`}
                            aria-checked={settings[key as keyof CookieSettings]}
                            className={`relative h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800 ${
                              settings[key as keyof CookieSettings] ? 'bg-blue-600' : 'bg-gray-600'
                            }`}
                          >
                            <span aria-hidden="true" className={`absolute top-1 left-1 h-3 w-3 rounded-full bg-white transition-transform ${
                              settings[key as keyof CookieSettings] ? 'translate-x-4' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleCustomize}
                    aria-expanded={showSettings}
                    aria-controls="cookie-settings"
                    className="rounded-md px-3 py-1.5 text-gray-300 hover:text-white transition-colors text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
                  >
                    {showSettings ? 'Hide' : 'Settings'}
                  </button>
                  {showSettings ? (
                    <button
                      type="button"
                      onClick={handleSaveSettings}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
                    >
                      Save
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleDeclineAll}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-md transition-colors text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
                      >
                        Decline
                      </button>
                      <button
                        type="button"
                        onClick={handleAcceptAll}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
                      >
                        Accept
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Demo App Component
// const App: React.FC = () => {
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
//       <main className="relative">
//         {/* Header */}
//         <header className="bg-white shadow-sm border-b">
//           <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//             <div className="flex justify-between items-center py-6">
//               <div className="flex items-center space-x-3">
//                 <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
//                   <span className="text-white font-bold text-lg">W</span>
//                 </div>
//                 <h1 className="text-2xl font-bold text-gray-900">Website</h1>
//               </div>
//               <nav className="hidden md:flex space-x-8">
//                 <a href="#" className="text-gray-700 hover:text-blue-600 transition-colors">Home</a>
//                 <a href="#" className="text-gray-700 hover:text-blue-600 transition-colors">About</a>
//                 <a href="#" className="text-gray-700 hover:text-blue-600 transition-colors">Services</a>
//                 <a href="#" className="text-gray-700 hover:text-blue-600 transition-colors">Contact</a>
//               </nav>
//             </div>
//           </div>
//         </header>

//         {/* Main Content */}
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//           <div className="text-center mb-12">
//             <h2 className="text-4xl font-bold text-gray-900 mb-4">Welcome to Our Website</h2>
//             <p className="text-xl text-gray-600 max-w-3xl mx-auto">
//               Experience our modern cookie consent system that respects user privacy while maintaining 
//               excellent user experience. This demo shows how the cookie banner integrates seamlessly 
//               with your main application content.
//             </p>
//           </div>

//           <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
//             {[1, 2, 3, 4, 5, 6].map((item) => (
//               <div key={item} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
//                 <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg mb-4"></div>
//                 <h3 className="text-xl font-semibold text-gray-900 mb-2">Feature {item}</h3>
//                 <p className="text-gray-600">
//                   This is a sample card that demonstrates how your main content appears with the cookie banner.
//                 </p>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Cookie Consent Component */}
//         <CookieConsent />
//       </main>
//     </div>
//   );
// };

// export default App;
